import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Amount from 'design-system/Amount';
import EmojiText from 'design-system/EmojiText';
import IconCircle from 'design-system/icons/IconCircle';
import ProgressBar from 'design-system/ProgressBar';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const Pot = ({ data, isFirstInSection, isLastInSection, isFirstItem }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const borderStyles = useMemo(() => [
        isFirstInSection && styles.firstInSection,
        isLastInSection && styles.lastItem,
        isFirstItem && styles.firstItem,
    ], [isFirstInSection, styles.firstInSection, isLastInSection, styles.lastItem, isFirstItem, styles.firstItem]);
    const handlePress = useCallback(() => {
        navigation.navigateDeprecated('Pot', { screenParams: data, hideSpaceUserAvatar: true });
    }, [data]);
    const goalText = useMemo(() => {
        if (data.goal) {
            const percent = ((data.balance.value / data.goal.amount) * 100).toFixed(1);
            return (<View style={styles.row}>
          <Text TextThin-14 Secondary>
            Achieved{' '}
          </Text>
          <Text NumbersThin-14 Secondary>
            {percent}%{' '}
          </Text>
          <Text TextThin-14 Secondary>
            of your{' '}
          </Text>
          <Amount Secondary NumbersThin-14 showFraction={false} value={{ value: data.goal.amount, currency: data.balance.currency }}/>
          <Text TextThin-14 Secondary>
            {' '}
            goal
          </Text>
        </View>);
        }
        return null;
    }, [data.balance.currency, data.balance.value, data.goal, styles.row]);
    return (<View style={[styles.wrapper, borderStyles]}>
      <TouchableHighlight hoverOnLight onCard={isWeb} style={isWeb ? undefined : borderStyles} activeOpacity={1} onPress={handlePress} underlayColor={colors.background.underlay}>
        <View style={styles.container}>
          <IconCircle width={44} height={44}>
            <EmojiText fontSize={20}>{data.emoji}</EmojiText>
          </IconCircle>
          <Spacer.W16 />
          <View style={styles.contentContainer}>
            <View style={styles.row}>
              <View style={styles.contentContainer}>
                <Text Text-16 flex numberOfLines={1}>
                  {data.type.startsWith('ACCUMULATED') ? 'Interest' : data.name}
                </Text>
              </View>
              <Spacer.W16 />
              {!data?.deletedAt && <Amount Numbers-16 value={data.balance}/>}
            </View>
            {!data?.deletedAt && data.goal ? (<>
                <Spacer h={4}/>
                {goalText}
                <Spacer.H8 />
                <ProgressBar style={styles.progressBarFlex} position={Number(((data.balance.value / data.goal.amount) * 100).toFixed(1))} colors={colors.gradients.ultimate.colors}/>
              </>) : null}
          </View>
        </View>
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
        // On web the card is padded by 8 and the row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    contentContainer: { flex: 1 },
    row: { flexDirection: 'row' },
    progressBarFlex: { flex: 0 },
    goalTextContainer: { flexDirection: 'row' },
    alignCenter: { alignItems: 'center' },
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
    // The first row under a section header, which has no rounded corners of its own
    firstInSection: {
        ...(isWeb && { paddingTop: rem(8) }),
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
}));
export default memo(Pot);
